if (!customElements.get('sticky-atc-bar')) {
  customElements.define(
    'sticky-atc-bar',
    class StickyAtcBar extends HTMLElement {
      static ADDED_STATE_DURATION = 2500;

      connectedCallback() {
        this.sectionId = this.dataset.sectionId;
        this.button = this.querySelector('.sticky-atc__button');
        this.buttonText = this.querySelector('.sticky-atc__button-text');
        this.spinner = this.querySelector('.loading__spinner');

        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);
        this.trigger = this.mainButton?.closest('.product-form__buttons') ?? this.mainButton;

        this.initScrollDetection();
        this.initButtonSync();
        this.initNotifyWatcher();

        this.button?.addEventListener('click', this.onButtonClick.bind(this));

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.onCartUpdate.bind(this));
        this.cartErrorUnsubscriber = subscribe(PUB_SUB_EVENTS.cartError, this.onCartError.bind(this));

        this.syncButton();
      }

      disconnectedCallback() {
        this.scrollObserver?.disconnect();
        this.buttonObserver?.disconnect();
        this.notifyObserver?.disconnect();
        cancelAnimationFrame(this.notifyFrame);
        this.variantChangeUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        this.cartErrorUnsubscriber?.();
        clearTimeout(this.addedTimeout);
      }

      /**
       * Reveal the bar only once the main add to cart button has scrolled above
       * the viewport. While the real button is still on screen — or still below
       * the fold — the bar stays hidden so it never covers the product media.
       */
      initScrollDetection() {
        if (!this.trigger) return;

        this.scrollObserver = new IntersectionObserver(
          (entries) => {
            const entry = entries[entries.length - 1];
            this.setVisible(!entry.isIntersecting && entry.boundingClientRect.top < 0);
          },
          { threshold: 0 }
        );

        this.scrollObserver.observe(this.trigger);
      }

      setVisible(visible) {
        const next = String(visible);
        if (this.dataset.visible === next) return;

        this.dataset.visible = next;
        this.toggleAttribute('inert', !visible);
      }

      /**
       * The bar's button submits the main product form, so the theme owns every
       * availability rule. Mirroring that button keeps the two labels, disabled
       * states and loading states identical without duplicating the logic.
       */
      initButtonSync() {
        if (!this.mainButton) return;

        this.buttonObserver = new MutationObserver(() => this.syncButton());
        this.buttonObserver.observe(this.mainButton, {
          attributes: true,
          attributeFilter: ['disabled', 'aria-disabled', 'class'],
          childList: true,
          subtree: true,
          characterData: true,
        });
      }

      syncButton() {
        if (!this.mainButton || !this.button) return;
        if (this.dataset.state === 'added') return;

        const disabled = this.mainButton.hasAttribute('disabled');
        const loading = this.mainButton.classList.contains('loading');
        const label = this.mainButton.querySelector(':scope > span')?.textContent.trim();

        this.syncPriceVisibility();

        // Sold out and the Back in Stock app has a notify button on the page:
        // offer it here instead of a dead "Sold out" button.
        if (disabled && this.findNotifyTarget()) {
          this.button.type = 'button';
          this.button.disabled = false;
          this.button.classList.remove('loading');
          this.button.setAttribute('aria-disabled', 'false');
          this.spinner?.classList.add('hidden');
          if (this.buttonText && this.dataset.notifyText) {
            this.buttonText.textContent = this.dataset.notifyText;
          }
          this.dataset.state = 'notify';
          return;
        }

        this.button.type = 'submit';
        this.button.disabled = disabled;
        this.button.classList.toggle('loading', loading);
        this.button.setAttribute('aria-disabled', String(disabled || loading));
        this.spinner?.classList.toggle('hidden', !loading);

        if (label && this.buttonText) this.buttonText.textContent = label;

        this.dataset.state = loading ? 'loading' : disabled ? 'disabled' : 'idle';
      }

      syncPriceVisibility() {
        const mainPrice = document.getElementById(`price-${this.sectionId}`);
        this.querySelector('.sticky-atc__price')?.classList.toggle(
          'hidden',
          mainPrice ? mainPrice.classList.contains('hidden') : false
        );
      }

      /**
       * The app injects its button after the page loads, and again whenever the
       * shopper switches variant, so re-check the product info subtree.
       */
      initNotifyWatcher() {
        const scope = document.getElementById(`ProductInfo-${this.sectionId}`);
        if (!scope || !this.dataset.notifySelector) return;

        this.notifyObserver = new MutationObserver(() => {
          cancelAnimationFrame(this.notifyFrame);
          this.notifyFrame = requestAnimationFrame(() => this.syncButton());
        });
        this.notifyObserver.observe(scope, { childList: true, subtree: true });
      }

      findNotifyTarget() {
        const selector = this.dataset.notifySelector;
        if (!selector) return null;

        const scope = document.getElementById(`ProductInfo-${this.sectionId}`) ?? document;
        const target = scope.querySelector(selector) ?? document.querySelector(selector);

        // Only offer it if the app actually rendered something visible.
        return target && target.getClientRects().length > 0 ? target : null;
      }

      onButtonClick(event) {
        if (this.dataset.state !== 'notify') return;

        event.preventDefault();

        const target = this.findNotifyTarget();
        if (!target) return;

        target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        target.click();
      }

      onVariantChange({ data: { sectionId, html } = {} } = {}) {
        if (sectionId !== this.sectionId) return;

        clearTimeout(this.addedTimeout);
        if (this.dataset.state === 'added') this.dataset.state = 'idle';

        const source = html?.getElementById(`StickyAtc-${this.sectionId}`);
        if (source) {
          this.copyHTML('.sticky-atc__price', source);
          this.copyHTML('.sticky-atc__media', source);
          this.copyText('.sticky-atc__variant', source);
        }

        this.syncButton();
      }

      copyHTML(selector, source) {
        const from = source.querySelector(selector);
        const to = this.querySelector(selector);
        if (from && to) to.innerHTML = from.innerHTML;
      }

      copyText(selector, source) {
        const from = source.querySelector(selector);
        const to = this.querySelector(selector);
        if (to) to.textContent = from ? from.textContent : '';
      }

      onCartUpdate(event) {
        if (event?.source !== 'product-form') return;
        if (!this.isCurrentVariant(event.productVariantId)) return;

        this.showAddedState();
      }

      onCartError(event) {
        if (event?.source !== 'product-form') return;

        clearTimeout(this.addedTimeout);
        this.dataset.state = 'idle';
        this.syncButton();
      }

      isCurrentVariant(variantId) {
        const input = document.getElementById(this.dataset.productFormId)?.querySelector('[name="id"]');
        return !input || String(input.value) === String(variantId);
      }

      showAddedState() {
        if (!this.button || !this.buttonText) return;

        clearTimeout(this.addedTimeout);

        this.dataset.state = 'added';
        this.button.classList.remove('loading');
        this.button.setAttribute('aria-disabled', 'false');
        this.spinner?.classList.add('hidden');

        const addedText = this.dataset.addedText;
        if (addedText) this.buttonText.textContent = addedText;

        this.addedTimeout = setTimeout(() => {
          this.dataset.state = 'idle';
          this.syncButton();
        }, StickyAtcBar.ADDED_STATE_DURATION);
      }
    }
  );
}
